import { API_BASE_URL } from '@synergy/config';
import {
    BonusCustomerCondition,
    BonusSettlementSupplierStatus,
    BonusSettlementType,
    getBonusSettlementSupplierTranslationId,
} from '@synergy/models';
import { BonusCustomerFilter, FilterModal, NoteToCustomerFilter, PoolChip, SearchFilter } from '@synergy/components';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloOverflowMenu } from '@synergy/components-palo';
import { PropsWithChildren, ReactNode, useState } from 'react';
import { useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { BonusSettlementSupplierDetailChangeAllBonusModal } from './components/bonus-settlement-supplier-detail-change-all-bonus-modal';
import { BonusSettlementSupplierDetailFormValues } from './models/bonus-settlement-supplier-detail-form-values';
import { BonusSettlementSupplierDetailInternalCommentModal } from './components/bonus-settlement-supplier-detail-internal-comment-modal';
import { PaloOverview } from '../../../common/components/palo/palo-overview';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { bonusSettlementsSupplierDetailRequestingSelector } from './store/bonus-settlements-supplier-detail-selector';
import { saveBonusSettlementSupplierDetail } from './store/bonus-settlements-supplier-detail-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

interface BonusSettlementSupplierDetailOverviewProps extends PropsWithChildren {
    /** Docked panel at the foot, where the detail sidebar used to be. */
    dock?: ReactNode;
    readOnly: boolean;
    year: number;
    supplierName: string;
    status: BonusSettlementSupplierStatus;
    type?: BonusSettlementType;
    bonusConditions: BonusCustomerCondition[];
}

export const BonusSettlementSupplierDetailOverview = ({
    readOnly,
    bonusConditions,
    status,
    year,
    supplierName,
    type,
    children,
    dock,
}: BonusSettlementSupplierDetailOverviewProps) => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const { bonusSettlementId, id: supplierBonusSettlementId } = useParams<{ bonusSettlementId: string; id: string }>();
    const requesting = useAppSelector(bonusSettlementsSupplierDetailRequestingSelector);
    const formikProps = useFormikContext<BonusSettlementSupplierDetailFormValues>();
    const [openChangeAllBonus, setOpenChangeAllBonus] = useState(false);
    const [openInternalCommentModal, setOpenInternalCommentModal] = useState(false);

    const handleSave = () => {
        if (bonusSettlementId && supplierBonusSettlementId) {
            dispatch(
                saveBonusSettlementSupplierDetail({
                    bonusSettlementId: +bonusSettlementId,
                    supplierBonusSettlementId: +supplierBonusSettlementId,
                    data: formikProps.values,
                })
            );
        }
        formikProps.resetForm({ values: formikProps.values });
    };

    const handleExport = () => {
        window.open(
            urlJoin(
                API_BASE_URL,
                `/bonus-settlements/${bonusSettlementId}/suppliers/${supplierBonusSettlementId}/export`
            ),
            '_self'
        );
    };

    return (
        <>
            <PaloOverview
                plain={true}
                dock={dock}
                // Plain text, so the bar can set its size and shorten it when
                // the name is longer than the room.
                titleMessage={
                    <>
                        {supplierName}&nbsp;
                        <Box component="span" color={type === BonusSettlementType.Reclaim ? 'error.main' : undefined}>
                            (
                            {type === BonusSettlementType.Reclaim ? (
                                <FormattedMessage
                                    id={'app.bonusSettlementsSupplierOverview.title.reclamation'}
                                    values={{ year: year }}
                                />
                            ) : (
                                year
                            )}
                            )
                        </Box>
                    </>
                }
                requesting={requesting}
                filters={
                    <>
                        <FilterModal>
                            <BonusCustomerFilter />
                            <NoteToCustomerFilter />
                        </FilterModal>
                        <SearchFilter />
                    </>
                }
                breadcrumbs={[
                    {
                        label: <FormattedMessage id="app.navigation.bonusSettlements" />,
                        onClick: () => navigate(ROUTES.bonusSettlements.overview),
                    },
                    {
                        label: <FormattedMessage id="app.bonusSettlements.tab.supplier" />,
                        onClick: () =>
                            navigate(ROUTES.bonusSettlements.detail.supplier.replace(':id', String(bonusSettlementId))),
                    },
                ]}
                additionalItemsRight={
                    <>
                        <PoolChip
                            listItem={true}
                            status={status}
                            label={
                                <FormattedMessage
                                    id={getBonusSettlementSupplierTranslationId(
                                        status ?? BonusSettlementSupplierStatus.Open
                                    )}
                                />
                            }
                        />
                        {/* One next step in the row, the rest behind the dots —
                            three toolbar buttons beside the title ran into the
                            crumb on a laptop (#2206). */}
                        <PaloOverflowMenu
                            actions={[
                                {
                                    label: (
                                        <FormattedMessage id="app.bonusSettlements.supplierDetail.internalComment" />
                                    ),
                                    onClick: () => setOpenInternalCommentModal(true),
                                },
                                {
                                    label: <FormattedMessage id="app.bonusSettlements.supplierDetail.changeAllBonus" />,
                                    reason: (
                                        <FormattedMessage id="app.bonusSettlements.supplierDetail.changeAllBonus.tooltip" />
                                    ),
                                    disabled: readOnly || (bonusConditions.length ?? 0) > 1,
                                    onClick: () => setOpenChangeAllBonus(true),
                                },
                                {
                                    label: <FormattedMessage id="app.bonusSettlements.supplierDetail.export" />,
                                    onClick: handleExport,
                                },
                            ]}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSave}
                            disabled={readOnly}
                        >
                            <FormattedMessage id="app.common.save" />
                        </Button>
                    </>
                }
            >
                {children}
            </PaloOverview>

            <BonusSettlementSupplierDetailChangeAllBonusModal
                open={openChangeAllBonus}
                onClose={() => setOpenChangeAllBonus(false)}
            />
            <BonusSettlementSupplierDetailInternalCommentModal
                open={openInternalCommentModal}
                onClose={() => setOpenInternalCommentModal(false)}
            />
        </>
    );
};
